import React, { useState, useEffect } from 'react';
import { 
  Facebook, 
  Eye, 
  EyeOff, 
  Save, 
  CheckCircle, 
  AlertCircle, 
  ExternalLink,
  Loader2,
  Info,
  BarChart3,
  Target,
  Zap,
  Shield,
  Copy,
  Check
} from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';

interface MetaPixelConfig {
  pixel_id: string;
  access_token: string;
  created_at?: string;
  updated_at?: string;
}

const MetaPixelPage: React.FC = () => {
  const [config, setConfig] = useState<MetaPixelConfig>({
    pixel_id: '',
    access_token: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [copiedPixelId, setCopiedPixelId] = useState(false);
  const [copiedToken, setCopiedToken] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await api.get('/api/meta-pixel/config');
      setConfig(response.data);
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
      toast.error('Erro ao carregar configurações do Meta Pixel');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!config.pixel_id.trim()) {
      toast.error('Pixel ID é obrigatório');
      return;
    }

    setSaving(true);
    try {
      await api.post('/api/meta-pixel/config', {
        pixel_id: config.pixel_id.trim(),
        access_token: config.access_token.trim()
      });
      
      toast.success('Configurações salvas com sucesso!');
      await fetchConfig();
    } catch (error: any) {
      console.error('Erro ao salvar configurações:', error);
      const message = error.response?.data?.error || 'Erro ao salvar configurações';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: keyof MetaPixelConfig, value: string) => {
    setConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const copyToClipboard = async (text: string, type: 'pixel' | 'token') => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'pixel') {
        setCopiedPixelId(true);
        setTimeout(() => setCopiedPixelId(false), 2000);
        toast.success('Pixel ID copiado!');
      } else {
        setCopiedToken(true);
        setTimeout(() => setCopiedToken(false), 2000);
        toast.success('Token copiado!');
      }
    } catch (error) {
      toast.error('Erro ao copiar');
    }
  };

  const validatePixelId = (pixelId: string): boolean => {
    // Meta Pixel ID geralmente tem 15-16 dígitos
    const cleaned = pixelId.replace(/\D/g, '');
    return cleaned.length >= 15 && cleaned.length <= 16;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48 md:h-64 lg:h-80">
        <div className="animate-spin rounded-full h-10 w-10 md:h-12 md:w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-3 md:space-y-4 lg:space-y-6 px-2 md:px-4 lg:px-0">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 md:p-5 lg:p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4">
          <div>
            <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2 md:gap-3">
              <Facebook className="h-6 w-6 md:h-7 md:w-7 lg:h-8 lg:w-8 text-blue-600" />
              Meta Pixel
            </h1>
            <p className="text-xs md:text-sm lg:text-base text-gray-600 dark:text-gray-400 mt-1">
              Configure o Meta Pixel para rastrear conversões e otimizar seus anúncios no Facebook e Instagram
            </p>
          </div>
        </div>
      </div>

      {/* Status do Pixel */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 md:p-5 lg:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 md:gap-4 mb-3 md:mb-4">
          <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-lg">
            <Facebook className="h-5 w-5 md:h-6 md:w-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-base md:text-lg lg:text-xl font-semibold text-gray-900 dark:text-white">
              Status do Meta Pixel
            </h3>
            <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400">
              {config.pixel_id ? 'Ativo e rastreando eventos' : 'Não configurado - configure para começar a rastrear'}
            </p>
          </div>
        </div>

        {config.pixel_id ? (
          <div className="p-3 md:p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 md:gap-3">
              <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                <CheckCircle className="h-4 w-4 md:h-5 md:w-5 flex-shrink-0" />
                <span className="text-xs md:text-sm font-medium">
                  Pixel ID: <span className="font-mono">{config.pixel_id}</span>
                </span>
              </div>
              <button
                onClick={() => copyToClipboard(config.pixel_id, 'pixel')}
                className="flex items-center gap-1.5 px-2 py-1.5 md:px-3 md:py-2 text-xs md:text-sm bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 rounded-lg hover:bg-green-200 dark:hover:bg-green-900/60 transition-colors"
              >
                {copiedPixelId ? (
                  <>
                    <Check className="h-3 w-3 md:h-4 md:w-4" />
                    Copiado!
                  </>
                ) : (
                  <>
                    <Copy className="h-3 w-3 md:h-4 md:w-4" />
                    Copiar
                  </>
                )}
              </button>
            </div>
            {config.updated_at && (
              <p className="text-xs text-green-600 dark:text-green-400 mt-2">
                Última atualização: {new Date(config.updated_at).toLocaleString('pt-BR')}
              </p>
            )}
          </div>
        ) : (
          <div className="p-3 md:p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <div className="flex items-start gap-2 md:gap-3">
              <AlertCircle className="h-4 w-4 md:h-5 md:w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs md:text-sm font-medium text-yellow-800 dark:text-yellow-300">
                  Meta Pixel não configurado
                </p>
                <p className="text-xs text-yellow-700 dark:text-yellow-400 mt-1">
                  Configure o Pixel ID abaixo para começar a rastrear conversões e otimizar seus anúncios
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Formulário de Configuração */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 md:p-5 lg:p-6">
        <h3 className="text-base md:text-lg lg:text-xl font-semibold text-gray-900 dark:text-white mb-4 md:mb-6 flex items-center gap-2">
          <Target className="h-5 w-5 text-blue-600" />
          Configurações do Meta Pixel
        </h3>

        <div className="space-y-4 md:space-y-6">
          {/* Pixel ID */}
          <div>
            <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
              Pixel ID <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                value={config.pixel_id}
                onChange={(e) => handleInputChange('pixel_id', e.target.value)}
                className={`w-full px-3 py-2 text-sm md:text-base border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white ${
                  config.pixel_id && !validatePixelId(config.pixel_id)
                    ? 'border-red-300 dark:border-red-700'
                    : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder="Ex: 123456789012345"
                required
              />
              {config.pixel_id && (
                <button
                  onClick={() => copyToClipboard(config.pixel_id, 'pixel')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  title="Copiar Pixel ID"
                >
                  {copiedPixelId ? (
                    <Check className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </button>
              )}
            </div>
            {config.pixel_id && !validatePixelId(config.pixel_id) && (
              <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                Pixel ID deve ter entre 15 e 16 dígitos
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Encontre seu Pixel ID no{' '}
              <a
                href="https://business.facebook.com/events_manager"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-1"
              >
                Meta Business Manager
                <ExternalLink className="h-3 w-3" />
              </a>
            </p>
          </div>

          {/* Access Token */}
          <div>
            <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
              Access Token <span className="text-gray-500 text-xs">(Opcional)</span>
            </label>
            <div className="relative">
              <input
                type={showToken ? 'text' : 'password'}
                value={config.access_token}
                onChange={(e) => handleInputChange('access_token', e.target.value)}
                className="w-full px-3 py-2 pr-20 text-sm md:text-base border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                placeholder="Token de acesso para eventos avançados"
              />
              <div className="absolute inset-y-0 right-0 flex items-center gap-1 pr-2">
                {config.access_token && (
                  <button
                    type="button"
                    onClick={() => copyToClipboard(config.access_token, 'token')}
                    className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    title="Copiar Token"
                  >
                    {copiedToken ? (
                      <Check className="h-3.5 w-3.5 text-green-500" />
                    ) : (
                      <Copy className="h-3.5 w-3.5" />
                    )}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setShowToken(!showToken)}
                  className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  title={showToken ? 'Ocultar token' : 'Mostrar token'}
                >
                  {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Token para enviar eventos personalizados via API (opcional, mas recomendado para melhor rastreamento)
            </p>
          </div>

          {/* Botão Salvar */}
          <div className="flex flex-col sm:flex-row justify-end gap-2 md:gap-3 pt-2">
            <button
              onClick={handleSave}
              disabled={saving || !config.pixel_id.trim() || (config.pixel_id.trim() ? !validatePixelId(config.pixel_id) : false)}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 border border-transparent text-sm md:text-base font-medium rounded-lg shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Salvar Configurações
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Informações sobre o Meta Pixel */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 md:p-5 lg:p-6">
        <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-4">
          <Info className="h-5 w-5 md:h-6 md:w-6 text-blue-600 dark:text-blue-400" />
          <h3 className="text-base md:text-lg lg:text-xl font-semibold text-blue-900 dark:text-blue-100">
            Como funciona o Meta Pixel?
          </h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 mb-4 md:mb-6">
          <div className="flex items-start gap-2 md:gap-3 p-3 md:p-4 bg-white dark:bg-gray-800/50 rounded-lg border border-blue-100 dark:border-blue-800/50">
            <div className="w-8 h-8 md:w-10 md:h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0 shadow-md">
              <Zap className="h-4 w-4 md:h-5 md:w-5 text-white" />
            </div>
            <div className="flex-1">
              <p className="text-xs md:text-sm font-semibold text-gray-900 dark:text-white mb-1">Rastreamento Automático</p>
              <p className="text-xs text-gray-600 dark:text-gray-300">O pixel rastreia automaticamente visualizações de produtos, adições ao carrinho e compras em tempo real</p>
            </div>
          </div>
          
          <div className="flex items-start gap-2 md:gap-3 p-3 md:p-4 bg-white dark:bg-gray-800/50 rounded-lg border border-blue-100 dark:border-blue-800/50">
            <div className="w-8 h-8 md:w-10 md:h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0 shadow-md">
              <Target className="h-4 w-4 md:h-5 md:w-5 text-white" />
            </div>
            <div className="flex-1">
              <p className="text-xs md:text-sm font-semibold text-gray-900 dark:text-white mb-1">Otimização de Anúncios</p>
              <p className="text-xs text-gray-600 dark:text-gray-300">O Meta usa esses dados para otimizar seus anúncios e encontrar clientes similares (Lookalike Audiences)</p>
            </div>
          </div>
          
          <div className="flex items-start gap-2 md:gap-3 p-3 md:p-4 bg-white dark:bg-gray-800/50 rounded-lg border border-blue-100 dark:border-blue-800/50 md:col-span-2 lg:col-span-1">
            <div className="w-8 h-8 md:w-10 md:h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center flex-shrink-0 shadow-md">
              <BarChart3 className="h-4 w-4 md:h-5 md:w-5 text-white" />
            </div>
            <div className="flex-1">
              <p className="text-xs md:text-sm font-semibold text-gray-900 dark:text-white mb-1">Relatórios Detalhados</p>
              <p className="text-xs text-gray-600 dark:text-gray-300">Veja métricas de conversão, ROI e performance dos seus anúncios no Meta Business Manager</p>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-3 md:pt-4 border-t border-blue-200 dark:border-blue-700">
          <div className="flex items-center gap-2 text-xs md:text-sm text-blue-800 dark:text-blue-200">
            <Shield className="h-4 w-4" />
            <span>Seus dados são protegidos e usados apenas para otimização de anúncios</span>
          </div>
          <a
            href="https://business.facebook.com/events_manager"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-3 py-2 md:px-4 md:py-2 text-xs md:text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium bg-white dark:bg-gray-800 rounded-lg border border-blue-200 dark:border-blue-700 hover:border-blue-300 dark:hover:border-blue-600 transition-colors"
          >
            <ExternalLink className="h-3.5 w-3.5 md:h-4 md:w-4" />
            Abrir Meta Business Manager
          </a>
        </div>
      </div>

      {/* Eventos Rastreados */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 md:p-5 lg:p-6">
        <h3 className="text-base md:text-lg lg:text-xl font-semibold text-gray-900 dark:text-white mb-3 md:mb-4 flex items-center gap-2">
          <Zap className="h-5 w-5 text-green-600" />
          Eventos Rastreados Automaticamente
        </h3>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
          <div className="p-3 md:p-4 bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800/50 rounded-lg">
            <div className="flex items-center gap-2 md:gap-3 mb-2">
              <div className="w-8 h-8 md:w-10 md:h-10 bg-green-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <CheckCircle className="h-4 w-4 md:h-5 md:w-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-xs md:text-sm font-semibold text-gray-900 dark:text-white">ViewContent</p>
                <p className="text-xs text-gray-600 dark:text-gray-400">Visualização de produtos</p>
              </div>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Disparado quando um cliente visualiza um produto no menu
            </p>
          </div>
          
          <div className="p-3 md:p-4 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800/50 rounded-lg">
            <div className="flex items-center gap-2 md:gap-3 mb-2">
              <div className="w-8 h-8 md:w-10 md:h-10 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <CheckCircle className="h-4 w-4 md:h-5 md:w-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-xs md:text-sm font-semibold text-gray-900 dark:text-white">AddToCart</p>
                <p className="text-xs text-gray-600 dark:text-gray-400">Adição ao carrinho</p>
              </div>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Disparado quando um produto é adicionado ao carrinho
            </p>
          </div>
          
          <div className="p-3 md:p-4 bg-purple-50 dark:bg-purple-900/10 border border-purple-200 dark:border-purple-800/50 rounded-lg">
            <div className="flex items-center gap-2 md:gap-3 mb-2">
              <div className="w-8 h-8 md:w-10 md:h-10 bg-purple-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <CheckCircle className="h-4 w-4 md:h-5 md:w-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-xs md:text-sm font-semibold text-gray-900 dark:text-white">InitiateCheckout</p>
                <p className="text-xs text-gray-600 dark:text-gray-400">Início do checkout</p>
              </div>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Disparado quando o cliente inicia o processo de pagamento
            </p>
          </div>
          
          <div className="p-3 md:p-4 bg-orange-50 dark:bg-orange-900/10 border border-orange-200 dark:border-orange-800/50 rounded-lg">
            <div className="flex items-center gap-2 md:gap-3 mb-2">
              <div className="w-8 h-8 md:w-10 md:h-10 bg-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <CheckCircle className="h-4 w-4 md:h-5 md:w-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-xs md:text-sm font-semibold text-gray-900 dark:text-white">Purchase</p>
                <p className="text-xs text-gray-600 dark:text-gray-400">Compra finalizada</p>
              </div>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Disparado quando uma compra é concluída com sucesso
            </p>
          </div>
        </div>

        <div className="mt-4 md:mt-6 p-3 md:p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-start gap-2 md:gap-3">
            <Info className="h-4 w-4 md:h-5 md:w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs md:text-sm font-medium text-gray-900 dark:text-white mb-1">
                Rastreamento em Tempo Real
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Todos os eventos são enviados automaticamente para o Meta assim que ocorrem, permitindo que você veja em tempo real como seus anúncios estão performando e otimize suas campanhas para maximizar conversões.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MetaPixelPage;
